import { Injectable } from '@angular/core';
import { API } from 'src/app/configs';
import { AuthState } from 'src/app/core/auth/auth-state';
import { AuthService } from 'src/app/core/auth/auth.service';
import { User } from 'src/app/core/auth/user';
import { HttpService } from 'src/app/core/http/http.service';
import { Person } from '../models/person';
import { State } from '../models/state';
import { Pagination } from '../utils/pagination';
import { HttpDao } from './http-dao';
import { GeneralService } from './general.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AppLoadingService } from './apploading.service';

@Injectable({
    providedIn: 'root'
})
export class PersonService extends HttpDao<PersonDto, number> {

    private loadedPerson: Person[] = [];

    protected person = new BehaviorSubject<any>([]);
    person$ = this.person.asObservable();

    public personDataOpen: any = {};

    protected action: BehaviorSubject<any> = new BehaviorSubject(null);
    public action$ = this.action.asObservable();

    constructor(httpService: HttpService,
        private authService: AuthService,
        private gs: GeneralService,
        protected als: AppLoadingService) {
        super(httpService, API.person.admin.edit);
    }

    updatePersonData(data) {
        this.person.next(this.convertToDto(data));
    }

    signUp(user: User, hash = false): Promise<any> {
        return this.httpService.post(!hash ? API.user.general.signup : API.user.general.signupinvited.setQueryParams({ hash: hash }), {
            name: user.name,
            password: user.password,
            email: user.email,
            cellphone: user.cellphone,
            creci_code: user.creci_code,
            creci_state_id: user.creci_state
        })
            .then(success => {
                this.authService.changeAuthState(!hash ? AuthState.CONFIRM_SIGN_UP : AuthState.SIGN_IN, user);
                return success;
            })
            .catch((err) => {
                let msg = this.gs.error(err);
                throw new Error(msg);
            });
    }

    loadPersons(sort: string, order: string, pageSize: number, pageIndex: number, search: [] = []): Promise<PersonList> {
        return this.httpService.post<PersonList>(API.person.admin.list, {
            type: 'edit',
            "sort_column": sort,
            "sort_direction": order,
            "total_rows": pageSize,
            "current_page": pageIndex,
            search: search
        });
    }

    async loadPerson(id: any): Promise<any> {
        if (!id)
            return Promise.resolve(false);

        await this.als.setLoaded(false, 'adminPersonLoad');
        return this.httpService.post<any>(API.person.admin.edit, id?.length == 36 ? { public_idtext: id } : { person_id: id })
            .then(result => {
                this.personDataOpen = this.convertFromDto(result[0]);
                return this.personDataOpen;
            })
            .catch(err => console.log(err))
            .finally(() => this.als.setLoaded(true, 'adminPersonLoad'));
    }

    async loadCompany(id: any): Promise<any> {
        if (!id)
            return Promise.resolve(false);

        await this.als.setLoaded(false, 'adminPersonLoad');
        return this.httpService.post<any>(API.person.general.companyview, { owner_person_id: id })
            .then(result => {
                this.personDataOpen = this.convertFromDto(result[0]);
                return this.personDataOpen;
            })
            .catch(err => console.log(err))
            .finally(() => this.als.setLoaded(true, 'adminPersonLoad'));
    }

    removePerson(id: number) {
        this.loadedPerson = this.loadedPerson.filter((value: Person) => {
            return value.id != id;
        })
    }

    getPerson(id: number): Person {
        return this.loadedPerson.filter((person: Person) => person.id === id)[0] || null;
    }

    convertFromDto(personDto: PersonDto): Person {
        let person = new Person();
        person.id = personDto.person_id;
        person.type = personDto.person_type;
        person.person_name = personDto.person_name;
        person.company_name = personDto.company_name;
        person.friendly_name = personDto.person_friendly_name;

        person.birthday = personDto?.birthday != "0000-00-00" && personDto?.birthday?.length == 10 ? personDto.birthday : '';
        if (person.birthday)
            person.birthday = person.birthday.substr(8, 2) + '/' + person.birthday.substr(5, 2) + '/' + person.birthday.substr(0, 4);

        if (person.type == Person.INDIVIDUAL) {
            person.document.number = personDto.document;
            person.document.verified = personDto.document_verified == 1;
        } else {
            person.document.number = personDto.document;
            person.document.verified = personDto.document_verified == 1;
        }

        person.creci.number = personDto.creci_code;
        person.creci.state = State.getStateByCode(this.gs.getGeneralData().states, personDto.creci_state_id);
        person.creci.verified = personDto.creci_verified == 1;

        person.cellphone.phone = personDto.cellphone;
        person.cellphone.verified = personDto.cellphone_verified == 1;
        person.cellphone.whatsapp = personDto.cellphone_whatsapp == 1;

        person.phone.phone = personDto.phone;
        person.phone.verified = personDto.phone_verified == 1;
        person.phone.whatsapp = personDto.phone_whatsapp == 1;

        person.email.email = personDto.email;
        person.email.verified = personDto.email_verified == 1;

        person.site = personDto.site;
        person.facebook = personDto.facebook;
        person.instagram = personDto.instagram;
        person.tiktok = personDto.tiktok;

        person.youtube = personDto.youtube;
        person.twitter = personDto.twitter;
        person.linkedin = personDto.linkedin;

        person.perfil_id = personDto.perfil_id;
        person.username = personDto.username;

        person.address.city.id = personDto.address_city_id;
        person.address.city.name = personDto.address_city_name;
        person.address.city.state = State.getStateByCode(this.gs.getGeneralData().states, personDto.address_state_id);
        person.address.district = personDto.address_district;
        person.address.complement = personDto.address_complement;
        person.address.latitude = personDto.address_latitude;
        person.address.longitude = personDto.address_longitude;
        person.address.number = personDto.address_number;
        person.address.street = personDto.address_street;
        person.address.zipcode = personDto.address_zipcode;

        person.files = personDto.files;

        person.about = personDto.about;
        person.status = personDto.status;


        person.verification = personDto.verification;

        person.deny_requesting = personDto.deny_requesting;

        person.visibility_score = personDto.visibility_score;
        person.allow_authorization_requests = personDto.allow_authorization_requests;
        person.allow_material_requests = personDto.allow_material_requests;
        person.allow_training_requests = personDto.allow_training_requests;
        person.allow_proposal_requests = personDto.allow_proposal_requests;

        return person;
    }

    convertToDto(person: Person): PersonDto {
        let personDto = new PersonDto();

        personDto.username = person.username;
        personDto.person_id = person.id;
        personDto.person_type = person.type;
        personDto.person_name = person.person_name;
        personDto.company_name = person.company_name;

        personDto.friendly_name = person.friendly_name;

        personDto.birthday = person?.birthday instanceof Date ? person.birthday.toISOString().split('T')[0] : person.birthday;//slice(0, 10)+' 00:00:01' : '';

        if (person.type == Person.INDIVIDUAL) {
            personDto.document = person.document.number;
            personDto.document_verified = person.document.verified ? 1 : 0;
        } else {
            personDto.document = person.document.number;
            personDto.document_verified = person.document.verified ? 1 : 0;
        }

        personDto.creci_code = person.creci.number;
        if (person.creci.state) {
            personDto.creci_state_id = person.creci.state.state_id.toString();
        }
        personDto.creci_verified = person.creci.verified ? 1 : 0;

        personDto.cellphone = person.cellphone.phone;
        personDto.cellphone_verified = person.cellphone.verified ? 1 : 0;
        personDto.cellphone_whatsapp = person.cellphone.whatsapp ? 1 : 0;

        personDto.phone = person.phone.phone;
        personDto.phone_verified = person.phone.verified ? 1 : 0;
        personDto.phone_whatsapp = person.phone.whatsapp ? 1 : 0;

        personDto.email = person.email.email;
        personDto.email_verified = person.email.verified ? 1 : 0;

        personDto.site = person.site;
        personDto.facebook = person.facebook;
        personDto.instagram = person.instagram;
        personDto.tiktok = person.tiktok;
        personDto.youtube = person.youtube;
        personDto.twitter = person.twitter;
        personDto.linkedin = person.linkedin;

        personDto.perfil_id = person.perfil_id;

        personDto.address_city_id = person.address.cityId;
        personDto.address_city_name = person.address.cityName;
        if (person.address.state) {
            personDto.address_state_id = person.address.state.state_id;
        }
        personDto.address_district = person.address.district;
        personDto.address_latitude = person.address.latitude;
        personDto.address_longitude = person.address.longitude;

        personDto.address_number = person.address.number;
        personDto.address_street = person.address.street;
        personDto.address_zipcode = person.address.zipcode;
        personDto.address_complement = person.address.complement;

        personDto.files = person.files;
        personDto.about = person.about;

        personDto.verification = person.verification;
        personDto.deny_requesting = person.deny_requesting;
        personDto.visibility_score = person.visibility_score;
        personDto.allow_authorization_requests = person.allow_authorization_requests;
        personDto.allow_material_requests = person.allow_material_requests;
        personDto.allow_training_requests = person.allow_training_requests;
        personDto.allow_proposal_requests = person.allow_proposal_requests;

        return personDto;
    }

    getStatus(id, type = 'person') {
        return this.gs.getStatus(type, false, id, 'name');
    }

    getPerfil(id, type = 'perfil') {
        return this.gs.getPerfil(type, false, id, 'perfil_name');
    }

    /***** ACTIONS *****/

    private buttonController = {
        ownerUpdate: [0, 1],
        update: [-1, 0, 1],
        delete: [-1, 0, 1],
        undelete: [-5],
        block: [0, 1, -5],
        unblock: [-1]
    };

    private buttonControllerToBack = {
        ownerUpdate: false,
        update: false,
        delete: true,
        undelete: false,
        block: true,
        unblock: true
    };

    private formControlDisabled = ["-5", "5", "2"];

    setActionSubscribe(fn): Subscription {
        if (!this.action.observers.length) {
            return this.action$.subscribe(fn);
        }
        return null;
    }

    setAction(action) {
        this.action.next(action);
    }

    getActionAllowed(action, adminType = []) {

        return typeof this.personDataOpen.status !== 'undefined' &&
            this.buttonController[action].indexOf(parseInt(this.personDataOpen.status)) > -1 &&
            (!adminType.length || adminType.indexOf(this.gs.getModule()) > -1);
    }

    getActionToBack(action) {
        return this.buttonControllerToBack[action];
    }

    getFormControlDisabled(): boolean {
        return this.formControlDisabled.indexOf(this.personDataOpen.status) == -1;
    }

    executeAction(action, data, fthen, fcatch, ffinally) {

        switch (action) {
            case 'ownerUpdate': return this.ownerUpdate(data).then(fthen).catch(fcatch).finally(ffinally);
            case 'update': return this.update(data).then(fthen).catch(fcatch).finally(ffinally);
            case 'delete': return this.delete(data).then(fthen).catch(fcatch).finally(ffinally);
            case 'undelete': return this.undelete(data).then(fthen).catch(fcatch).finally(ffinally);
            case 'block': return this.block(data).then(fthen).catch(fcatch).finally(ffinally);
            case 'unblock': return this.unblock(data).then(fthen).catch(fcatch).finally(ffinally);
            default: return false;
        }
    }

    ownerUpdate(obj: PersonDto): Promise<any> {
        if (!API.ad[this.gs.getModule()].owner)
            throw new Error("Access denied");

        return this.httpService.put<any>(API.ad[this.gs.getModule()].owner, { owner_person_id: obj.person_id });
    }
    update(obj: PersonDto): Promise<any> {
        if (!API.person[this.gs.getModule()].base)
            throw new Error("Access denied");

        if (obj?.person_id == 0)
            return this.httpService.post<any>(API.person[this.gs.getModule()].base, obj);

        return this.httpService.put<any>(API.person[this.gs.getModule()].base, obj);
    }

    delete(obj: any): Promise<any> {
        if (!API.person[this.gs.getModule()].base)
            throw new Error("Access denied");

        obj.status = -5;

        return this.httpService.put<any>(API.person[this.gs.getModule()].base, obj);
    }

    undelete(obj: any): Promise<any> {
        if (!API.person[this.gs.getModule()].base)
            throw new Error("Access denied");

        obj.status = 1;

        return this.httpService.put<any>(API.person[this.gs.getModule()].base, obj);
    }

    block(obj: any): Promise<any> {
        if (!API.person[this.gs.getModule()].base)
            throw new Error("Access denied");

        obj.status = -1;

        return this.httpService.put<any>(API.person[this.gs.getModule()].base, obj);
    }

    unblock(obj: any): Promise<any> {
        if (!API.person[this.gs.getModule()].base)
            throw new Error("Access denied");

        obj.status = 1;

        return this.httpService.put<any>(API.person[this.gs.getModule()].base, obj);
    }
}

export class PersonList {

    list: PersonDto[];
    total: Pagination;

}

export class PersonDto {

    username: string;
    person_id: number;
    person_type: string;
    person_name: string;
    company_name: string;
    friendly_name: string;
    person_friendly_name: string;
    birthday: string;

    document: string;
    document_verified: number;

    creci_code: string;
    creci_state_id: string;
    creci_verified: number;

    cellphone: string;
    cellphone_verified: number;
    cellphone_whatsapp: number;

    phone: string;
    phone_verified: number;
    phone_whatsapp: number;

    email: string;
    email_verified: number;

    perfil_id: string;
    modules: any[];
    site: string;
    facebook: string;
    instagram: string;
    tiktok: string;
    youtube: string;
    twitter: string;
    linkedin: string;

    address_city_id: number;
    address_city_name: string;
    address_complement: string;
    address_country_id: string;
    address_district: string;
    address_latitude: number;
    address_longitude: number;
    address_number: string;
    address_state_id: number;
    address_street: string;
    address_zipcode: string;

    status: number;

    data: string;
    creation: string;

    files: any[];

    about: string;

    verification: any;

    deny_requesting: any;
    visibility_score: any;
    allow_authorization_requests: any;
    allow_material_requests: any;
    allow_training_requests: any;
    allow_proposal_requests: any;
}